'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { cx } from '../../../internal/cx.js';
import { CheckGlyph, CopyGlyph, WarningGlyph } from '../../../internal/icons.js';
import { Button } from '../button/index.js';
/**
 * Fallback for browsers or contexts without the async Clipboard API (for
 * example an insecure origin). Uses a temporary off-screen textarea and
 * restores the previous focus and selection.
 */
function legacyCopy(text) {
    if (typeof document === 'undefined' || typeof document.execCommand !== 'function')
        return false;
    const active = document.activeElement;
    const selection = document.getSelection();
    const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.setAttribute('aria-hidden', 'true');
    area.style.position = 'fixed';
    area.style.top = '0';
    area.style.left = '-9999px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
        ok = document.execCommand('copy');
    }
    catch {
        ok = false;
    }
    area.remove();
    if (range && selection) {
        selection.removeAllRanges();
        selection.addRange(range);
    }
    active?.focus?.({ preventScroll: true });
    return ok;
}
/** Writes text to the clipboard: the async Clipboard API first, then the legacy fallback. */
export async function writeToClipboard(text) {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (clipboard?.writeText) {
        try {
            await clipboard.writeText(text);
            return;
        }
        catch (error) {
            if (legacyCopy(text))
                return;
            throw error instanceof Error ? error : new Error(String(error));
        }
    }
    if (legacyCopy(text))
        return;
    throw new Error('Clipboard is not available in this browser.');
}
/**
 * Copies text and tracks the result. The state machine is
 * `idle → copied | error → idle` (after `resetAfter`). It never throws:
 * failures go to `onError` and `status: 'error'`, so the caller decides how
 * to tell the user (inline, a toast, a manual-copy fallback).
 */
export function useCopyToClipboard(options = {}) {
    const { resetAfter = 2000 } = options;
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const timer = useRef(undefined);
    const mounted = useRef(true);
    const callbacks = useRef(options);
    callbacks.current = options;
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(timer.current);
        };
    }, []);
    const reset = useCallback(() => {
        clearTimeout(timer.current);
        setStatus('idle');
        setError(null);
    }, []);
    const copy = useCallback(async (value) => {
        clearTimeout(timer.current);
        let next;
        try {
            const text = typeof value === 'function' ? await value() : value;
            await writeToClipboard(text);
            if (!mounted.current)
                return true;
            setError(null);
            next = 'copied';
            callbacks.current.onCopy?.(text);
        }
        catch (raw) {
            const err = raw instanceof Error ? raw : new Error(String(raw));
            if (!mounted.current)
                return false;
            setError(err);
            next = 'error';
            callbacks.current.onError?.(err);
        }
        setStatus(next);
        if (resetAfter > 0) {
            timer.current = setTimeout(() => {
                if (mounted.current)
                    setStatus('idle');
            }, resetAfter);
        }
        return next === 'copied';
    }, [resetAfter]);
    return { copy, status, error, reset };
}
/**
 * Copies a value to the clipboard, shows a short "Copied" state and announces
 * the result through a polite live region. Errors are reported to the caller
 * through `onCopyError`; the button never fails silently.
 */
export const CopyButton = forwardRef(function CopyButton({ value, label = 'Copy', copiedLabel = 'Copied', errorLabel = "Couldn't copy", iconOnly = false, target, copiedMessage = 'Copied to clipboard', errorMessage = "Couldn't copy. Select the text and copy it manually.", icon, resetAfter = 2000, onCopy, onCopyError, onClick, variant = iconOnly ? 'ghost' : 'secondary', className, ...rest }, ref) {
    const { copy, status } = useCopyToClipboard(onCopy || onCopyError
        ? {
            resetAfter,
            ...(onCopy ? { onCopy } : {}),
            ...(onCopyError ? { onError: onCopyError } : {}),
        }
        : { resetAfter });
    // The live region keeps its text until the next attempt, then is cleared and
    // refilled so repeated copies are announced each time.
    const [message, setMessage] = useState('');
    const glyph = status === 'copied' ? (_jsx(CheckGlyph, {})) : status === 'error' ? (_jsx(WarningGlyph, {})) : ((icon ?? _jsx(CopyGlyph, {})));
    return (_jsxs(_Fragment, { children: [_jsxs(Button, { ref: ref, ...rest, variant: variant, "aria-label": iconOnly ? (target ? `${label} ${target}` : label) : undefined, title: rest.title ?? (iconOnly ? (target ? `${label} ${target}` : label) : undefined), "data-state": status, "data-icon-only": iconOnly || undefined, className: cx('mn-copy-button', className), onClick: (event) => {
                    onClick?.(event);
                    if (event.defaultPrevented)
                        return;
                    setMessage('');
                    void copy(value).then((ok) => setMessage(ok ? copiedMessage : errorMessage));
                }, children: [_jsx("span", { className: "mn-copy-button__icon", "aria-hidden": true, children: glyph }), iconOnly ? null : (
                    // The idle and copied labels share one grid cell, so the button keeps
                    // the width of the longer one and does not jump on success. The rarer
                    // error label replaces them and may widen the button.
                    _jsx("span", { className: "mn-copy-button__label", children: status === 'error' ? (_jsx("span", { "data-active": "", children: errorLabel })) : ([
                            ['idle', label],
                            ['copied', copiedLabel],
                        ].map(([key, text]) => (_jsx("span", { "data-active": key === status || undefined, "aria-hidden": key === status ? undefined : true, children: text }, key)))) })), !iconOnly && target ? _jsxs("span", { className: "mn-visually-hidden", children: [" ", target] }) : null] }), _jsx("span", { className: "mn-visually-hidden", role: "status", "aria-live": "polite", "aria-atomic": "true", children: message })] }));
});
//# sourceMappingURL=copy-button.js.map